import React, { FunctionComponent, useRef } from 'react';
import { Box, Divider, Grid } from '@mui/material';
import CustomPagination from '../CustomPagination';
import DebouncedLoader from '../DebouncedLoader';
import NoResultsOverlay from './CustomNoResultsOverlay';
import ResourcesCard from './ResourcesCard';
import { useResourceLibraryItems } from '../../../graphql/pimcore/modules/resourceLibrary/contexts/ResourceLibraryItemsProvider';
import { useResourceTypes } from '../../../graphql/pimcore/modules/resourceLibrary/contexts/ResourceTypesProvider';
import { useBrands } from '../../../graphql/pimcore/modules/resourceLibrary/contexts/BrandsProvider';
import { useResponsive } from '../../../contexts/ResponsiveContext';

interface ResourcesListProps {
    view?: string;
    backgroundColor?: string;
    isTechnicalResources?: boolean;
}

const ResourcesList: FunctionComponent<ResourcesListProps> = ({
    view,
    backgroundColor,
    isTechnicalResources,
}) => {
    const {
        filteredItems,
        filteredItemsRelevanceMap,
        paginatedItems,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        loading: itemsLoading,
    } = useResourceLibraryItems();

    const { loading: typeLoading } = useResourceTypes();
    const { loading: brandLoading } = useBrands();
    const topRef = useRef<HTMLDivElement | null>(null);

    const loading = itemsLoading || typeLoading || brandLoading;
    const { isScreenMd, isScreenSm, isScreenXs } = useResponsive();
    const isSmallScreen = isScreenMd || isScreenSm || isScreenXs;
    // Map items, attaching missing search terms from the relevance map.
    const documents = paginatedItems
        ? paginatedItems.map((item) => {
              const missing = filteredItemsRelevanceMap.get(item.id)?.missingTerms;
              return { ...item, missingSearchTerms: missing || [] };
          })
        : [];

    const handlePageChange = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number
    ) => {
        setCurrentPage(newPage + 1);
    };

    const showThumbnails = view === 'module';

    return (
        <div>
            <DebouncedLoader
                loading={loading}
                delay={0}
                size={40}
                sx={
                    isTechnicalResources
                        ? { marginLeft: '30rem', marginTop: '1.5rem', color: '#B75332' }
                        : { margin: '8rem 0', color: '#B75332' }
                }
            >
                {documents.length === 0 && !itemsLoading ? (
                    <Box sx={{ margin: '8rem 0' }}>
                        <NoResultsOverlay />
                    </Box>
                ) : (
                    <>
                        <div ref={topRef} data-scroll-anchor />
                        <Box sx={{ marginTop: '1rem', marginLeft: '1rem' }}>
                            {documents.length > 0 && (
                                <CustomPagination
                                    count={filteredItems.length}
                                    page={currentPage - 1}
                                    onPageChange={handlePageChange}
                                    rowsPerPage={itemsPerPage}
                                    // scrollAnchorRef={topRef}
                                    // autoScrollTop
                                    // loading={loading}
                                    // scrollOffset={99}
                                />
                            )}
                        </Box>
                        <Grid
                            container
                            spacing={2}
                            direction="column"
                            display="flex"
                            width={'100%'}
                        >
                            {documents.map((doc, index) => (
                                <Grid
                                    item
                                    xs={12}
                                    key={doc.id}
                                    marginLeft={isSmallScreen ? '0rem' : '2rem'}
                                    width={'100%'}
                                >
                                    <ResourcesCard
                                        item={doc}
                                        backgroundColor={backgroundColor}
                                        showThumbnail={showThumbnails}
                                        clickableHeader={true}
                                        displayAssetLinks={true}
                                        hideLanguageVariantsCondition={'never'}
                                        hideViewAssetLabelCondition={'always'}
                                    />
                                    {index < documents.length - 1 && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Divider sx={{ width: '90%' }} />
                                        </Box>
                                    )}
                                </Grid>
                            ))}
                        </Grid>
                        <Box sx={{ marginTop: '1rem', marginBottom: '1rem', marginLeft: '1rem' }}>
                            {documents.length > 0 && (
                                <CustomPagination
                                    count={filteredItems.length}
                                    page={currentPage - 1}
                                    onPageChange={handlePageChange}
                                    rowsPerPage={itemsPerPage}
                                    scrollAnchorRef={topRef}
                                    autoScrollTop
                                    loading={loading}
                                    scrollOffset={99}
                                />
                            )}
                        </Box>
                    </>
                )}
            </DebouncedLoader>
        </div>
    );
};

export default ResourcesList;
